/**
 * m3 echo Frontend - Teaser-Analyse
 *
 * Schnellanalyse für Stadtwerke mit Historie. Admins wählen den Mandanten.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import { useCanEdit, useIsAdmin, useUser } from '../stores/useAuthStore';
import { tenantApi } from '../services/admin';
import type { TenantOption } from '../services/admin';
import {
  teaserErrorMessage,
  teaserErrorStatus,
  useDownloadTeaserCSV,
  useTeaserAnalyses,
  useTeaserDefaults,
  useTeaserModels,
  useTeaserModuleActive,
} from '../hooks/useTeaser';
import TeaserHistory from '../components/teaser/TeaserHistory';
import NewTeaserAnalysisDialog from '../components/teaser/NewTeaserAnalysisDialog';
import TeaserDefaultPromptsDialog from '../components/teaser/TeaserDefaultPromptsDialog';

const NOT_ENABLED = 'Die Teaser-Analyse ist für diesen Mandanten nicht freigeschaltet.';

const TeaserAnalysis: React.FC = () => {
  const isAdmin = useIsAdmin();
  const canEdit = useCanEdit();
  const user = useUser();
  const { active: moduleActive, isLoading: moduleLoading } = useTeaserModuleActive();

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState<number | undefined>(undefined);
  const [tenantsLoading, setTenantsLoading] = useState(isAdmin);
  const [page, setPage] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Admin: Mandantenliste laden, eigener Mandant vorausgewählt, sonst der erste
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    tenantApi
      .getOptions()
      .then((res) => {
        if (cancelled) return;
        setTenants(res.data);
        const own = res.data.find((t) => t.id === user?.tenant_id);
        setTenantId((own ?? res.data[0])?.id);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.tenant_id]);

  const ready = isAdmin ? tenantId !== undefined : true;
  const enabled = ready && (isAdmin || moduleActive);

  const analysesQuery = useTeaserAnalyses(tenantId, page, enabled);
  const defaultsQuery = useTeaserDefaults(tenantId, enabled);
  const modelsQuery = useTeaserModels(tenantId, enabled);
  const download = useDownloadTeaserCSV(tenantId);

  const forbidden = teaserErrorStatus(analysesQuery.error) === 403;
  const blocked = !isAdmin && !moduleLoading && !moduleActive;

  const handleTenantChange = (id: number) => {
    // Dialoge schließen und zurücksetzen, Seite auf Anfang
    setNewOpen(false);
    setDefaultsOpen(false);
    setPage(0);
    setTenantId(id);
  };

  const handleDownload = (id: number) => {
    download.mutate(id, {
      onError: (e) => setDownloadError(teaserErrorMessage(e, 'Download fehlgeschlagen')),
    });
  };

  if (moduleLoading || tenantsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (blocked || forbidden) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Teaser-Analyse
        </Typography>
        <Alert severity="warning">{NOT_ENABLED}</Alert>
      </Box>
    );
  }

  const models = modelsQuery.data ?? [];
  const defaults = defaultsQuery.data;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">Teaser-Analyse</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="teaser-tenant-label">Mandant</InputLabel>
              <Select
                labelId="teaser-tenant-label"
                label="Mandant"
                value={tenantId ?? ''}
                onChange={(e) => handleTenantChange(Number(e.target.value))}
              >
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.display_name || t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canEdit && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setNewOpen(true)}
                disabled={!enabled}
              >
                Neue Analyse
              </Button>
              <Tooltip title="Default-Fragen">
                <span>
                  <IconButton
                    aria-label="Default-Fragen"
                    onClick={() => setDefaultsOpen(true)}
                    disabled={!enabled}
                  >
                    <SettingsIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Stack>
      </Stack>

      {analysesQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {analysesQuery.isError && (
        <Alert severity="error">
          {teaserErrorMessage(analysesQuery.error, 'Analysen konnten nicht geladen werden')}
        </Alert>
      )}
      {analysesQuery.data && (
        <TeaserHistory
          items={analysesQuery.data.items}
          total={analysesQuery.data.total}
          page={page}
          onPageChange={setPage}
          models={models}
          onDownload={handleDownload}
          downloadingId={download.isPending ? (download.variables ?? null) : null}
        />
      )}

      <NewTeaserAnalysisDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onStarted={() => {
          setPage(0);
          setSnackbar('Analyse gestartet');
        }}
        defaults={defaults}
        defaultsLoading={defaultsQuery.isLoading}
        defaultsError={defaultsQuery.isError}
        models={models}
        tenantId={tenantId}
      />
      <TeaserDefaultPromptsDialog
        open={defaultsOpen}
        onClose={() => setDefaultsOpen(false)}
        defaults={defaults}
        defaultsLoading={defaultsQuery.isLoading}
        defaultsError={defaultsQuery.isError}
        tenantId={tenantId}
      />

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
      <Snackbar
        open={downloadError !== null}
        autoHideDuration={6000}
        onClose={() => setDownloadError(null)}
      >
        <Alert severity="error" onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeaserAnalysis;
